import React, {useMemo} from 'react'
import styled from 'styled-components'
import Box, {BoxProps} from '../Box'
import sx, {SxProp} from '../../sx'
import {themeGet} from '../../utils/theme-get'
import {RuleStepConnectorProps, Direction, TestIdType} from './types'

const ConnectorWrapper = styled(Box)<BoxProps & SxProp & RuleStepConnectorProps>`
  position: relative;
  transition-property: border-color;
  transition-duration: normal;
  ${sx}
`
const StyledLine = styled(Box)<BoxProps & RuleStepConnectorProps>`
  border-left-width: ${p => (p.isSelectedStep ? '1.3px' : '1px')};
  border-left-color: ${p => {
    if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected')
    if (p.isLastStep) return 'transparent'
    return themeGet('colors.border.accent.gray')
  }};
  border-left-style: ${p => (!p.isLastStep ? 'dashed' : '')};
  /* min-height: 36px; */
  /* height: 100%; */
`
const ArrowWrapper = styled(Box)<BoxProps & AngleArrowProps>`
  position: absolute;

  &[data-direction='right'] {
    transform: rotate(90deg);
    bottom: 0;
    top: 100%;
    left: 15px;
  }
  &[data-direction='left'] {
    transform: rotate(-90deg);
    bottom: 0;
    top: 100%;
    left: -15px;
  }
  &[data-direction='down'] {
    bottom: 0;
    top: calc(100% + 12px);
    transform: rotate(180deg);
  }
`
/* the connector arrow line itself */
const Line = styled(Box)<BoxProps & AngleArrowProps>`
  border-right-width: ${p => (p.isSelectedStep ? '1.3px' : '1px')};
  border-right-style: dashed;

  border-color: ${p => {
    if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected')
    if (p.isLastStep) return 'transparent'
    return themeGet('colors.border.accent.gray')
  }};
  display: inline-block;

  &[data-direction='right'] {
    height: 3px;
  }
  &[data-direction='left'] {
    height: 3px;
  }
  &[data-direction='down'] {
    height: 2px;
  }
`
/* the connector arrow tip */
const TipRight = styled(Box)<BoxProps & AngleArrowProps>`
  position: absolute;
  top: 5px;
  left: 2px;
  height: 6px;
  border-right-width: ${p => (p.isSelectedStep ? '1.3px' : '1px')};
  border-right-style: dashed;
  border-color: ${p => {
    if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected')
    if (p.isLastStep) return 'transparent'
    return themeGet('colors.border.accent.gray')
  }};
  display: inline-block;
  transform: rotate(-45deg);
  &[data-direction='right'] {
    bottom: 0;
    top: 5px;
  }
  &[data-direction='left'] {
    bottom: 0;
    top: 5px;
  }
  &[data-direction='down'] {
    bottom: 0;
    top: 0;
  }
`
const TipLeft = styled(Box)<BoxProps & AngleArrowProps>`
  position: absolute;
  top: 5px;
  right: 2px;
  height: 6px;
  border-right-width: ${p => (p.isSelectedStep ? '1.3px' : '1px')};
  border-right-style: dashed;
  border-color: ${p => {
    if (p.isSelectedStep || p.isCompletedStep) return themeGet('colors.border.selected')
    if (p.isLastStep) return 'transparent'
    return themeGet('colors.border.accent.gray')
  }};
  display: inline-block;
  transform: rotate(45deg);
  &[data-direction='right'] {
    bottom: -6px;
    top: 5px;
  }
  &[data-direction='left'] {
    bottom: 0;
    top: 5px;
  }
  &[data-direction='down'] {
    bottom: 0;
    top: 0;
  }
`

type AngleArrowProps = {
  direction?: Direction
  index?: number
  isCompletedStep?: boolean
  isSelectedStep?: boolean
  isLastStep?: boolean
} & TestIdType

const AngleArrow = ({
  direction,
  index,
  isCompletedStep,
  isSelectedStep,
  isLastStep,
  testId,
  ...props
}: AngleArrowProps) => {
  const angleArrowLineTestId = `${testId}-lozenge`
  return (
    <ArrowWrapper
      as="div"
      index={index}
      isSelectedStep={isSelectedStep}
      data-direction={direction}
      data-testid={angleArrowLineTestId}
    >
      <TipLeft as="span" index={index} isSelectedStep={isSelectedStep} isCompletedStep={isCompletedStep} />
      <Line
        as="span"
        index={index}
        data-direction={direction}
        isSelectedStep={isSelectedStep}
        isCompletedStep={isCompletedStep}
        isLastStep={isLastStep}
        {...props}
      />
      <TipRight as="span" index={index} isSelectedStep={isSelectedStep} isCompletedStep={isCompletedStep} />
    </ArrowWrapper>
  )
}

const RuleStepConnector = ({
  isLastStep,
  isSelectedStep: isSelectedProp,
  isCompletedStep,
  children,
  direction = 'right',
  index,
  testId,
  ...props
}: RuleStepConnectorProps & SxProp) => {
  const isActive = isSelectedProp
  // @TODO: if we add an icon, dynamically calculate
  const getMargin = () => 2

  const styles = useMemo(() => {
    const base = {
      marginBottom: 2,
      marginTop: 2,
      paddingBottom: 4,
      marginRight: 0,
      height: 'auto',
      alignSelf: 'stretch',
      borderTopWidth: 0,
      borderInlineStartWidth: isLastStep ? 0 : '2px',
      minHeight: isLastStep ? 'auto' : '1.5rem',
      borderLeftWidth: isLastStep ? 0 : '2px',
      borderLeftStyle: 'solid',
      marginLeft: '18px'
    }
    return base
  }, [isLastStep])

  const lineTestId = `${testId}-lozenge`
  const arrowTestId = `${testId}-arrow`

  return (
    <ConnectorWrapper data-highlighted={Boolean(isActive)} data-testid={lineTestId} {...props}>
      {isLastStep ? (
        <StyledLine index={index} isSelectedStep={isActive} isLastStep={isLastStep} data-direction={direction}>
          {children}
        </StyledLine>
      ) : (
        <>
          <StyledLine
            index={index}
            isSelectedStep={isActive}
            isCompletedStep={isCompletedStep}
            data-direction={direction}
          >
            {children}
          </StyledLine>
          <AngleArrow
            index={index}
            direction={direction}
            isSelectedStep={isActive}
            isCompletedStep={isCompletedStep}
            testId={arrowTestId}
          />
        </>
      )}
    </ConnectorWrapper>
  )
}

RuleStepConnector.displayName = 'RuleStepConnector'
export default RuleStepConnector
